import React, { useEffect, useState } from "react";
import "../SaveBar/SaveBar.css";

// Pop-up for one important action (archive, restore, …), same look as the
// save pop-up: confirm → working… → done ✓  (or the problem, with Try again).
//
// Props:
//   title, message      – the question and a short explanation
//   item                – optional { image, name, sub } shown so it's clear what's affected
//   confirmText, tone   – e.g. "Yes, archive", "danger" | "primary" | "success"
//   icon                – emoji for the confirm step
//   onConfirm()         – does the action; returns { ok: true } or { ok: false, message }
//   workingText, doneTitle, doneText
//   onClose()           – called when the pop-up closes
const ActionPopup = ({
  title, message, item, confirmText = "Yes", tone = "primary", icon = "❓",
  onConfirm, workingText = "Working…", doneTitle = "Done!", doneText = "", onClose,
}) => {
  const [phase, setPhase] = useState("confirm"); // confirm | working | done | failed
  const [error, setError] = useState("");

  const run = async () => {
    setPhase("working");
    const result = (await onConfirm()) || { ok: false, message: "Something went wrong" };
    if (result.ok) setPhase("done");
    else { setError(result.message); setPhase("failed"); }
  };

  useEffect(() => {
    if (phase !== "done") return;
    const t = setTimeout(onClose, 1800);
    return () => clearTimeout(t);
  }, [phase, onClose]);

  useEffect(() => {
    if (phase === "working") return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, onClose]);

  const close = () => phase !== "working" && onClose();

  const Item = () => item ? (
    <div className="savepop-item">
      {item.image ? <img src={item.image} alt="" /> : <span className="savepop-item-noimg">🛍️</span>}
      <div>
        <strong>{item.name}</strong>
        {item.sub && <small>{item.sub}</small>}
      </div>
    </div>
  ) : null;

  return (
    <div className="savepop-overlay" onClick={close}>
      <div className={`savepop phase-${phase}`} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        {phase === "confirm" && (
          <>
            <div className={`savepop-icon tone-${tone}`}>{icon}</div>
            <h3>{title}</h3>
            {message && <p>{message}</p>}
            <Item />
            <div className="savepop-actions">
              <button type="button" className="savebar-btn light" onClick={close}>Cancel</button>
              <button type="button" className={`savebar-btn ${tone}`} onClick={run} autoFocus>{confirmText}</button>
            </div>
          </>
        )}

        {phase === "working" && (
          <>
            <div className="savepop-icon"><i className="savepop-spinner" /></div>
            <h3>{workingText}</h3>
            <Item />
            <div className="savepop-progress"><span /></div>
          </>
        )}

        {phase === "done" && (
          <>
            <div className="savepop-icon ok">✓</div>
            <h3>{doneTitle}</h3>
            {doneText && <p>{doneText}</p>}
            <div className="savepop-actions center">
              <button type="button" className="savebar-btn primary" onClick={onClose} autoFocus>Done</button>
            </div>
          </>
        )}

        {phase === "failed" && (
          <>
            <div className="savepop-icon error">!</div>
            <h3>Couldn't finish</h3>
            <p className="savepop-message">{error}</p>
            <p className="savepop-note">Nothing was changed.</p>
            <div className="savepop-actions center">
              <button type="button" className="savebar-btn light" onClick={run}>Try again</button>
              <button type="button" className="savebar-btn primary" onClick={onClose} autoFocus>OK</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActionPopup;
